import { tPath, fPath, gPath, ePath, currentPlatform, PLATFORMS, type Platform } from './paths';

type Navigator = (path: string) => void;

// Notification/background-job action_urls are authored backend-side as bare
// paths with no indication of which of the 3 frontend apps (Terminus,
// FoundersHub, Entrepreneur) they target. `/foundershub/...` is the one
// self-describing convention producers already use; these bare paths are
// the only ones ever set on entrepreneur-targeted notifications (approval,
// rejection, project-match, Gaia meetings). `/documents/{id}` (Gaia
// documents) is matched as a prefix: no Terminus route lives under it.
// Anything else defaults to Terminus, matching every other notification
// producer in the codebase.
const ENTREPRENEUR_ONLY_PATHS = new Set(['/dashboard', '/profile', '/matched-opportunities', '/meetings']);
const ENTREPRENEUR_PREFIXES = ['/documents'];
const FOUNDERSHUB_PREFIX = '/foundershub';
const GAIA_PREFIX = '/gaia';

function resolveHref(url: string): string {
  if (url.startsWith(`${FOUNDERSHUB_PREFIX}/`) || url === FOUNDERSHUB_PREFIX) {
    return fPath(url.slice(FOUNDERSHUB_PREFIX.length) || '/');
  }
  if (url.startsWith(`${GAIA_PREFIX}/`) || url === GAIA_PREFIX) {
    return gPath(url.slice(GAIA_PREFIX.length) || '/');
  }
  // A producer may append a query to any of these (meeting notifications emit
  // `/meetings?meeting=…` deep-links), so route on the pathname alone and
  // re-attach the query to the resolved path.
  const queryAt = url.indexOf('?');
  const pathname = queryAt === -1 ? url : url.slice(0, queryAt);
  const search = queryAt === -1 ? '' : url.slice(queryAt);
  if (ENTREPRENEUR_ONLY_PATHS.has(pathname) || ENTREPRENEUR_PREFIXES.some((prefix) => pathname.startsWith(`${prefix}/`))) {
    return ePath(`${pathname}${search}`);
  }
  return tPath(url);
}

/** The platform a resolved (prefix-mode) path names, or null when it names
 * none — same-host mode never prefixes, and a handful of cross-platform
 * surfaces (`/profile`, `/org`, …) don't either. */
function platformOfPath(path: string): Platform | null {
  const first = path.split('/')[1];
  return (PLATFORMS as readonly string[]).includes(first) ? (first as Platform) : null;
}

/**
 * Open a backend-authored notification action URL, resolving it to the
 * correct one of the 3 frontend apps (see `resolveHref`) and correctly
 * cross-host-redirecting when that app is served from a different origin
 * than the current page.
 *
 * - `http(s)://…` URLs open in a new tab as-is
 * - bare paths resolve via `tPath`/`fPath`/`ePath`: a same-host result
 *   is a relative path (routed through `navigate` when in a Router context,
 *   else `window.location.href`); a cross-host result is a fully
 *   qualified URL and always uses `window.location.href` — React Router's
 *   `navigate` cannot reach another origin.
 *
 * 🔴 INVARIANT: every route table in `config/resources.ts` (and both panels'
 * app-switcher hrefs) is computed ONCE at module load from `currentPlatform()`
 * — safe only because changing the platform prefix has always cost a full
 * document load elsewhere in the app (`PlatformPanelRedirect`,
 * `LegacyPlatformSectionRedirect` in App.tsx). A resolved target that carries
 * a DIFFERENT platform's prefix than the one we are on must leave the same
 * way, via `window.location.href`, never `navigate()` — an SPA navigation
 * would land the user on the new platform while every catalog-derived href
 * (the sidebar, the app switcher) stays frozen on the old one. Do not
 * "simplify" this back to a plain `navigate()`.
 */
export function openNotificationLink(url: string, navigate?: Navigator): void {
  if (url.startsWith('http')) {
    window.open(url, '_blank');
    return;
  }
  const target = resolveHref(url);
  if (target.startsWith('http') || !navigate) {
    window.location.href = target;
    return;
  }
  const targetPlatform = platformOfPath(target);
  if (targetPlatform && targetPlatform !== currentPlatform()) {
    window.location.href = target;
    return;
  }
  navigate(target);
}
